import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { CaseSummary, Page } from '@/api/types';
import { Banner, Card, ErrorNote, Money, Reference, Spinner, Timestamp } from '@/components/Common';
import { SeverityPill } from '@/components/StatusPill';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/lib/auth';
import { money, relativeAge } from '@/lib/format';

/**
 * The exceptions queue — the operational heart of the console (spec §13). Oldest first, because
 * the oldest case is the one whose customer has been waiting longest.
 */
export function ExceptionsPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [openCase, setOpenCase] = useState<CaseSummary | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['exceptions'],
    queryFn: () => api.get<Page<CaseSummary>>('/api/admin/exceptions?size=100'),
    refetchInterval: 20_000,
  });

  const { data: reconciler } = useQuery({
    queryKey: ['reconciler'],
    queryFn: () => api.get<{ halted: boolean }>('/api/admin/reconciler/status'),
    refetchInterval: 20_000,
  });

  const resume = useMutation({
    mutationFn: () => api.post('/api/admin/reconciler/resume'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reconciler'] }),
  });

  const bulkReconcile = useMutation({
    mutationFn: async (cases: CaseSummary[]) => {
      // Sequential on purpose: a burst of forced reconciliations against a sick upstream is how a
      // bad morning becomes a worse one.
      for (const item of cases) {
        await api.post(`/api/admin/orders/${item.orderId}/reconcile`).catch(() => undefined);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['exceptions'] }),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const cases = data?.content ?? [];
  const breached = cases.filter((item) => item.slaBreached).length;

  return (
    <div className="space-y-4">
      {reconciler?.halted && (
        <Banner tone="danger">
          <div className="flex items-center justify-between gap-4">
            <span>
              <strong>Reconciliation is halted.</strong> More reversals landed in five minutes than
              the anomaly threshold allows, which usually means the query endpoint is wrong rather
              than that the recharges vanished. Confirm before restarting — everything stays
              UNKNOWN until you do.
            </span>
            {can('ADMIN', 'OPS') && (
              <button type="button" className="btn-danger shrink-0"
                      disabled={resume.isPending} onClick={() => resume.mutate()}>
                {resume.isPending ? 'Restarting…' : 'Restart reconciler'}
              </button>
            )}
          </div>
        </Banner>
      )}

      {breached > 0 && (
        <Banner tone="warn">
          <strong>{breached}</strong> case{breached === 1 ? ' has' : 's have'} breached their SLA and
          been escalated one severity level.
        </Banner>
      )}

      <Card
        title={`Open cases — ${cases.length}`}
        actions={
          can('ADMIN', 'OPS', 'SUPPORT') && cases.length > 0 ? (
            <button type="button" className="btn-secondary" disabled={bulkReconcile.isPending}
                    onClick={() => bulkReconcile.mutate(cases)}>
              {bulkReconcile.isPending ? 'Reconciling…' : 'Force reconcile all'}
            </button>
          ) : null
        }
      >
        {cases.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-ink-500">
            The queue is empty. That is a claim worth making deliberately — the daily close will say
            the same thing at 23:00.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-ink-200">
                <tr>
                  <th className="th">Case</th>
                  <th className="th">Sev</th>
                  <th className="th">Category</th>
                  <th className="th">Order</th>
                  <th className="th text-right">Impact</th>
                  <th className="th text-right">Age</th>
                  <th className="th text-right">Attempts</th>
                  <th className="th">SLA due</th>
                  <th className="th">Last message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {cases.map((item) => (
                  <tr key={item.id}
                      className={`cursor-pointer hover:bg-ink-50 ${item.slaBreached ? 'bg-red-50' : ''}`}
                      onClick={() => setOpenCase(item)}>
                    <td className="td mono font-medium">{item.caseNumber}</td>
                    <td className="td"><SeverityPill severity={item.severity} /></td>
                    <td className="td text-ink-700">{item.category.replace(/_/g, ' ')}</td>
                    <td className="td" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/orders/${item.orderId}`} className="hover:underline">
                        <Reference value={item.agentReference} />
                      </Link>
                    </td>
                    <td className="td text-right">
                      <Money amount={item.financialImpact ? money(item.financialImpact) : null} />
                    </td>
                    <td className="td tabular text-right">{relativeAge(
                      new Date(Date.now() - item.ageMinutes * 60_000).toISOString())}</td>
                    <td className="td tabular text-right">{item.attemptCount}</td>
                    <td className={`td ${item.slaBreached ? 'font-semibold text-red-700' : ''}`}>
                      <Timestamp value={item.slaDueAt} />
                    </td>
                    <td className="td max-w-xs truncate text-ink-600"
                        title={item.lastUpstreamMessage ?? ''}>
                      {item.lastUpstreamMessage ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {openCase && <CaseModal item={openCase} onClose={() => setOpenCase(null)} />}
    </div>
  );
}

interface CaseDetail {
  case: CaseSummary;
  notes: Array<{ author: string; note: string; createdAt: string }>;
}

function CaseModal({ item, onClose }: { item: CaseSummary; onClose: () => void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [resolution, setResolution] = useState('MARKED_DELIVERED');

  const { data } = useQuery({
    queryKey: ['exception', item.id],
    queryFn: () => api.get<CaseDetail>(`/api/admin/exceptions/${item.id}`),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['exception', item.id] });
    queryClient.invalidateQueries({ queryKey: ['exceptions'] });
  };

  const addNote = useMutation({
    mutationFn: () => api.post(`/api/admin/exceptions/${item.id}/notes`, { note }),
    onSuccess: () => { setNote(''); invalidate(); },
  });

  const resolve = useMutation({
    mutationFn: () => api.post(`/api/admin/exceptions/${item.id}/resolve`, {
      resolution, note, financialImpact: item.financialImpact,
    }),
    onSuccess: () => { invalidate(); onClose(); },
  });

  const escalationPack = useMutation({
    mutationFn: () => api.get<Record<string, unknown>>(
      `/api/admin/exceptions/${item.id}/escalation-pack`),
    onSuccess: (pack) => {
      const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${item.caseNumber}-escalation.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  return (
    <Modal title={`${item.caseNumber} — ${item.category.replace(/_/g, ' ')}`} onClose={onClose}>
      <div className="rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
        <div className="label text-sky-700">First action</div>
        {item.firstAction}
      </div>

      {item.balanceCorroboration && (
        <p className="mt-3 text-xs text-ink-600">
          Balance corroboration: <strong>{item.balanceCorroboration}</strong> — advisory only, it
          never overrides what the transaction query said.
        </p>
      )}

      <div className="mt-4">
        <div className="label">Timeline</div>
        <ol className="mt-1 max-h-48 space-y-2 overflow-y-auto">
          {(data?.notes ?? []).map((entry, index) => (
            <li key={index} className="rounded bg-ink-50 px-3 py-2 text-sm">
              <div className="flex justify-between text-xs text-ink-500">
                <span>{entry.author}</span>
                <Timestamp value={entry.createdAt} />
              </div>
              <p className="mt-1 text-ink-800">{entry.note}</p>
            </li>
          ))}
          {(data?.notes ?? []).length === 0 && (
            <li className="text-sm text-ink-500">No notes yet.</li>
          )}
        </ol>
      </div>

      <label className="mt-4 block">
        <span className="label">Add a note</span>
        <textarea className="input mt-1" rows={2} value={note}
                  placeholder="What did you check, and what did it show?"
                  onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" className="btn-secondary"
                disabled={!note.trim() || addNote.isPending}
                onClick={() => addNote.mutate()}>Add note</button>
        <button type="button" className="btn-secondary" disabled={escalationPack.isPending}
                onClick={() => escalationPack.mutate()}>
          {escalationPack.isPending ? 'Building…' : 'Escalation pack'}
        </button>
      </div>

      {can('ADMIN', 'OPS') && (
        <div className="mt-5 border-t border-ink-200 pt-4">
          <label className="block">
            <span className="label">Resolution</span>
            <select className="input mt-1" value={resolution}
                    onChange={(e) => setResolution(e.target.value)}>
              <option value="MARKED_DELIVERED">Marked delivered</option>
              <option value="MARKED_NOT_DELIVERED">Marked not delivered</option>
              <option value="NO_ACTION_NEEDED">No action needed</option>
              <option value="WRITTEN_OFF">Write off</option>
            </select>
          </label>
          {resolution === 'WRITTEN_OFF' && (
            <p className="mt-2 text-xs text-amber-800">
              A write-off above the configured value needs a second ADMIN to approve it first, and
              the approver cannot be the person writing it off.
            </p>
          )}
          {resolve.isError && <ErrorNote error={resolve.error} />}
          <button type="button" className="btn-danger mt-3"
                  disabled={!note.trim() || resolve.isPending}
                  onClick={() => resolve.mutate()}>
            {resolve.isPending ? 'Closing…' : 'Close case'}
          </button>
          <p className="mt-1 text-xs text-ink-500">A reason is required and is audited.</p>
        </div>
      )}
    </Modal>
  );
}
